import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Bell, Mail } from "lucide-react";

export default function NotifPrefs() {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/notifications/preferences").then((r) => setPrefs(r.data)).catch(() => {});
  }, []);

  const patch = async (key, value) => {
    setBusy(true);
    const prev = prefs;
    setPrefs({ ...prefs, [key]: value });
    try {
      const { data } = await api.patch("/notifications/preferences", { [key]: value });
      setPrefs(data);
      toast.success("Reminder settings updated");
    } catch (e) {
      setPrefs(prev);
      toast.error("Update failed");
    } finally {
      setBusy(false);
    }
  };

  if (!prefs) return null;

  return (
    <div className="border-2 border-black/20 p-6 space-y-5 mb-10" data-testid="notif-prefs">
      <div className="flex items-center gap-2">
        <Bell className="w-4 h-4" />
        <div className="text-xs uppercase tracking-[0.3em] font-bold">Daily practice reminders</div>
      </div>
      <p className="text-xs font-mono text-neutral-500">
        We'll nudge you if you skip a day so your streak stays alive. Toggle either channel off anytime.
      </p>

      <div className="flex items-center justify-between border border-black/10 p-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 border border-black/20 flex items-center justify-center bg-white">
            <Mail className="w-4 h-4" />
          </div>
          <div>
            <div className="text-sm font-black tracking-tight">Email reminders</div>
            <div className="text-[11px] font-mono text-neutral-500">One short email on days you miss practice</div>
          </div>
        </div>
        <Switch
          checked={prefs.email_reminders_enabled}
          onCheckedChange={(v) => patch("email_reminders_enabled", v)}
          disabled={busy}
          data-testid="notif-email-toggle"
        />
      </div>

      <div className="flex items-center justify-between border border-black/10 p-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 border border-black/20 flex items-center justify-center bg-white">
            <Bell className="w-4 h-4" />
          </div>
          <div>
            <div className="text-sm font-black tracking-tight">In-app reminders</div>
            <div className="text-[11px] font-mono text-neutral-500">Banner on the dashboard if you haven't practiced today</div>
          </div>
        </div>
        <Switch
          checked={prefs.browser_reminders_enabled}
          onCheckedChange={(v) => patch("browser_reminders_enabled", v)}
          disabled={busy}
          data-testid="notif-browser-toggle"
        />
      </div>
    </div>
  );
}
